import { Box, Theme, useTheme } from "@mui/material";

// ----------------------------------------------------------------------

export type StatusTone = "success" | "warning" | "error" | "neutral";

/** Mapeamento status→tom do handoff (seção "Status (badges)") */
const STATUS_TONES: Record<string, StatusTone> = {
  ativo: "success",
  ativa: "success",
  publicado: "success",
  publicada: "success",
  "concluído": "success",
  "concluída": "success",
  "válido": "success",
  valido: "success",
  "em produção": "success",
  "em producao": "success",
  agendado: "warning",
  agendada: "warning",
  "em teste": "warning",
  rascunho: "warning",
  beta: "warning",
  pendente: "warning",
  "em análise": "warning",
  "em analise": "warning",
  expirado: "error",
  expirada: "error",
  esgotado: "error",
  cancelado: "error",
  cancelada: "error",
  inativo: "error",
  inativa: "error",
  "sem plano": "error",
  falhou: "error",
  bloqueado: "error",
};

/** Mapeia o texto do status para um tom. Case-insensitive (normaliza com trim+lowercase). */
export function statusTone(status: string): StatusTone {
  return STATUS_TONES[status.trim().toLowerCase()] ?? "neutral";
}

const toneStyles = (theme: Theme, tone: StatusTone) => {
  switch (tone) {
    case "success":
      return { color: theme.palette.success.main, backgroundColor: theme.palette.brand.successBg };
    case "warning":
      return { color: theme.palette.warning.main, backgroundColor: theme.palette.brand.warningBg };
    case "error":
      return { color: theme.palette.error.main, backgroundColor: theme.palette.brand.errorBg };
    default:
      return { color: theme.palette.text.secondary, backgroundColor: theme.palette.action.selected };
  }
};

interface StatusBadgeProps {
  status: string;
  /** Força um tom específico em vez do mapeado pelo texto */
  tone?: StatusTone;
}

/** Badge estática de status (pill + bolinha). O texto do status é o conteúdo acessível; em contextos ambíguos, forneça contexto externo (ex.: cabeçalho da coluna). */
export default function StatusBadge({ status, tone }: StatusBadgeProps) {
  const theme = useTheme();
  const styles = toneStyles(theme, tone ?? statusTone(status));

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.375,
        borderRadius: "100px",
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...styles,
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }}
      />
      {status}
    </Box>
  );
}
